import { IOnInit, IQService } from "angular";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../server/serverUtility";
import { IQuery } from "../../server/query";
import { UserProfile } from "../../../application/UserProfile";
import {
  ChangeJournalObjectContainerEntry,
  vsServiceName as ChangeJournalViewItemServiceName,
} from "./itemObjectDetail";
import { ChangeJournalViewItemService } from "./itemObjectDetail/service";
import { ChangeJournalObjectContainer } from "./objectContainer";
import { groupBy, uniq } from "lodash";
import {
  ChangeJournalLogItem,
  ChangeJournalObject,
  ChangeJournalObjectIdentifier,
  ChangeJournalRecord,
  ChangeJournalObjectIdentifierEqual,
} from "./models";

export class ChangeJournalViewComponentController implements IOnInit {
  static $inject = [ServerUtilityName, ChangeJournalViewItemServiceName, "$q"];

  objectContainer!: ChangeJournalObjectContainer;

  constructor(
    private readonly serverUtility: ServerUtility,
    private readonly changeJournalViewItemService: ChangeJournalViewItemService,
    private readonly $q: IQService
  ) {}

  /*bindings*/
  objects!: ChangeJournalObject[];
  /*end bindings*/

  private url: string = "/api/changeLog";
  private logItems: ChangeJournalLogItem[] = [];

  currentUsers: UserProfile[] = [];
  readonly query: IQuery = {};

  $onInit(): void {
    this.objectContainer = new ChangeJournalObjectContainer(
      this.changeJournalViewItemService,
      this.getInitialContainerEntries(this.objects),
      this.$q
    );
    this.query.ObjectIdentifiers = this.objects.map((o) => o.id);
    this.query.OrderBy = "-Id";
    this.query.Take = 10000;

    this.fetchRecords();
  }

  getInitialContainerEntries(
    objects: ChangeJournalObject[]
  ): ChangeJournalObjectContainerEntry[] {
    return objects.map((i) => this.getEntryFromObject(i));
  }

  private getEntryFromObject(
    object: ChangeJournalObject
  ): ChangeJournalObjectContainerEntry {
    return {
      id: object.id,
      promise: this.$q.when(object),
    };
  }

  fetchChangeJournalRecords(query: IQuery) {
    return this.serverUtility.fetchAllPost<ChangeJournalRecord>({
      postObject: query,
      url: this.url,
    });
  }

  private mapJournalRecordToLogItem(
    changeJournalRecord: ChangeJournalRecord
  ): ChangeJournalLogItem {
    return {
      objectIdentifier: this.getObjectIdentifier(changeJournalRecord),
      changeJournalObject: undefined,
      changeJournalRecord: changeJournalRecord,
      relatedItems: [],
    };
  }

  private getObjectIdentifier(
    changeJournalRecord: ChangeJournalRecord
  ): ChangeJournalObjectIdentifier {
    return {
      ObjectId: changeJournalRecord.ObjectId,
      ObjectName: changeJournalRecord.ObjectName,
    };
  }

  setPromisesForChangeJournalObject(logItems: ChangeJournalLogItem[]) {
    return this.$q.all(
      logItems.map((li) => this.setPromiseForChangeJournalObject(li))
    );
  }

  private setPromiseForChangeJournalObject(logItem: ChangeJournalLogItem) {
    return this.objectContainer
      .getObjectEntry(logItem.objectIdentifier)
      .promise.then((o) => (logItem.changeJournalObject = o));
  }

  fetchRecords() {
    this.fetchChangeJournalRecords(this.query)
      .then((result) => {
        const logItems = result.map((i) => this.mapJournalRecordToLogItem(i));
        this.setPromisesForChangeJournalObject(logItems);
        return logItems;
      })
      .then((logItems) => {
        const correlationIds = uniq(
          logItems.map((li) => li.changeJournalRecord.CorrelationId)
        );

        //fetch basic information about related records
        this.fetchChangeJournalRecords({
          CorrelationIds: correlationIds,
        }).then((allRelatedRecords) => {
          const recordByCorrelationId = groupBy(
            allRelatedRecords,
            (r) => r.CorrelationId
          );
          logItems.forEach((logItem) => {
            const relatedRecords = (
              recordByCorrelationId[
                logItem.changeJournalRecord.CorrelationId
              ] ?? []
            )
              .filter(
                (r) =>
                  !ChangeJournalObjectIdentifierEqual(
                    this.getObjectIdentifier(logItem.changeJournalRecord),
                    this.getObjectIdentifier(r)
                  )
              )
              .map((i) => this.mapJournalRecordToLogItem(i));

            logItem.relatedItems = relatedRecords;
          });
        });

        this.logItems = logItems;
      });
  }

  queryUsers(search: string) {
    this.serverUtility
      .fetchAllPost<UserProfile>({
        url: this.url + "/users",
        postObject: {
          ObjectIdentifiers: { ...this.query.ObjectIdentifiers },
          OrderBy: "DisplayName",
          Take: 10000,
          NameContains: search,
        },
      })
      .then((r) => {
        this.currentUsers = r;
      });
  }
}
